import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketAPI } from '../services/api';
import { FiPlus, FiLogOut, FiSearch, FiFilter, FiGrid, FiList } from 'react-icons/fi';
import './Dashboard.css';

const Dashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [ticketFilter, setTicketFilter] = useState('all'); // 'all' or 'my-tickets'
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, ticketFilter]); // Re-fetch when status or ticket filter changes

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Only filter by user if "My Tickets" is selected
      if (ticketFilter === 'my-tickets') {
        params.user_id = user.id;
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await ticketAPI.getAllTickets(params);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredTickets = tickets.filter(ticket =>
    ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.ticket_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status, isWithdrawn) => {
    if (isWithdrawn) return 'badge-withdrawn';
    switch (status) {
      case 'open':
        return 'badge-open';
      case 'in-progress':
        return 'badge-in-progress';
      case 'resolved':
        return 'badge-resolved';
      case 'closed':
        return 'badge-closed';
      default:
        return 'badge-open';
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <h1 className="dashboard-title">Ticket Dashboard</h1>
              <p className="dashboard-subtitle">Welcome back, {user.full_name}</p>
            </div>
            <div className="header-right">
              <button onClick={() => navigate('/create-ticket')} className="btn btn-primary">
                <FiPlus /> New Ticket
              </button>
              <button onClick={handleLogout} className="btn btn-secondary">
                <FiLogOut /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-content container">
        <div className="filters-section">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="ticket-filter-toggle">
            <button
              className={`filter-button ${ticketFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTicketFilter('all')}
              title="Show all team tickets"
            >
              👥 All Tickets
            </button>
            <button
              className={`filter-button ${ticketFilter === 'my-tickets' ? 'active' : ''}`}
              onClick={() => setTicketFilter('my-tickets')}
              title="Show only my tickets"
            >
              👤 My Tickets
            </button>
          </div>

          <div className="view-toggle">
            <button
              className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <FiList />
            </button>
            <button
              className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View (Tiles)"
            >
              <FiGrid />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="spinner"></div>
        ) : filteredTickets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎫</div>
            <h3>No tickets found</h3>
            <p>Create your first ticket to get started</p>
            <button onClick={() => navigate('/create-ticket')} className="btn btn-primary">
              <FiPlus /> Create Ticket
            </button>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'tickets-grid' : 'tickets-list'}>
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                className={`ticket-card card ${ticket.is_withdrawn ? 'withdrawn' : ''} ${viewMode === 'list' ? 'list-view' : ''}`}
                onClick={() => navigate(`/ticket/${ticket.id}`)}
              >
                <div className="ticket-header">
                  <span className={`ticket-id ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                    {ticket.ticket_id}
                  </span>
                  <span className={`badge ${getStatusBadge(ticket.status, ticket.is_withdrawn)}`}>
                    {ticket.is_withdrawn ? 'Withdrawn' : ticket.status}
                  </span>
                </div>

                {viewMode === 'list' && (
                  <>
                    <div className="ticket-type">
                      {ticket.complaint_type_name}
                    </div>
                    <div className="ticket-creator">
                      By: {ticket.user_name}
                    </div>
                  </>
                )}

                <h3 className={`ticket-title ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                  {ticket.title}
                </h3>
                
                <p className={`ticket-description ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                  {ticket.description.substring(0, viewMode === 'list' ? 150 : 100)}
                  {ticket.description.length > (viewMode === 'list' ? 150 : 100) ? '...' : ''}
                </p>

                {viewMode === 'list' && (
                  <div className="ticket-created">
                    Created: {formatDate(ticket.created_at)}
                  </div>
                )}

                {viewMode !== 'list' && (
                  <div className="ticket-meta">
                    <div className="meta-item">
                      <span className="meta-label">Type:</span>
                      <span className="meta-value">{ticket.complaint_type_name}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Created:</span>
                      <span className="meta-value">{formatDate(ticket.created_at)}</span>
                    </div>
                  </div>
                )}

                {ticket.attachments && ticket.attachments.length > 0 && (
                  <div className="ticket-attachments">
                    <span className="attachment-count">
                      📎 {ticket.attachments.length} attachment(s)
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;